import { Box, Grid2 } from '@mui/material';
import React, { useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl';

import SkeletonLoader from '@components/SkeletonLoader';

import { HealthScoreType } from '@constants/health-score-info';

import { useLatestHealthScores } from '@hooks/api/health-score';

import { DashHeader } from '../Common.style';
import { HeaderSpacing } from '../Routines/Routines.style';
import HealthScoreCard from './HealthScoreCard';

export interface HealthMetric {
  id: HealthScoreType;
  name: MessageDescriptor;
  score: number | null;
  date: string | null | undefined;
}

const messages = defineMessages({
  healthScores: {
    id: 'healthScores.title',
    defaultMessage: 'HEALTH SCORES',
  },
  cardiovascular: {
    id: 'healthScores.cardiovascular.title',
    defaultMessage: 'Cardiovascular',
  },
  focus: {
    id: 'healthScores.focus.title',
    defaultMessage: 'Focus',
  },
  respiratory: {
    id: 'healthScores.respiratory.title',
    defaultMessage: 'Respiratory',
  },
  immune: {
    id: 'healthScores.immune.title',
    defaultMessage: 'Immunity Index',
  },
  sleep: {
    id: 'healthScores.sleep.title',
    defaultMessage: 'Sleep Quality',
  },
  stress: {
    id: 'healthScores.stress.title',
    defaultMessage: 'Stress Level',
  },
});
const HealthScores: React.FC = () => {
  const { formatMessage } = useIntl();

  const { data, loading } = useLatestHealthScores({
    //focus_score: true,
    //cardiovascular_score: true,
    immunity_score: true,
    respiratory_score: true,
    stress_score: true,
    sleep_quality_score: true,
  });

  const healthData = useMemo(() => {
    /*
    const staticHealthData = [
      new ObservationRecord({
        resourceType: 'Observation',
        status: 'final', // required
        code: {
          coding: [
            {
              code: HealthScoreType.Sleep,
              display: 'Sleep',
              system: 'https://vitall.com/documentation',
            },
          ],
        },
        effectiveDateTime: '2025-10-27T23:10:00.000Z',
        valueQuantity: {
          value: 80,
          unit: '',
          code: HealthScoreType.Sleep,
          system: 'https://vitall.com/documentation',
        },
      }),
      new ObservationRecord({
        resourceType: 'Observation',
        status: 'final', // required
        code: {
          coding: [
            {
              code: HealthScoreType.Respiratory,
              display: 'Respiratory',
              system: 'https://vitall.com/documentation',
            },
          ],
        },
        effectiveDateTime: '2025-10-27T23:10:00.000Z',
        valueQuantity: {
          value: 90,
          unit: '',
          code: HealthScoreType.Respiratory,
          system: 'https://vitall.com/documentation',
        },
      }),
    ];
    
    const healthScores = {
      ...data,
      [HealthScoreType.Sleep]: staticHealthData[0],
      [HealthScoreType.Respiratory]: staticHealthData[1],
    };
    */

    const healthTypes = [
      {
        id: HealthScoreType.Cardiovascular,
        name: messages.cardiovascular,
        obs: data['cardiovascular-score'],
      },
      {
        id: HealthScoreType.Focus,
        name: messages.focus,
        obs: data['focus-score'],
      },
      {
        id: HealthScoreType.Respiratory,
        name: messages.respiratory,
        obs: data['respiratory-score'],
      },
      {
        id: HealthScoreType.Immune,
        name: messages.immune,
        obs: data['immunity-score'],
      },
      {
        id: HealthScoreType.Sleep,
        name: messages.sleep,
        obs: data['sleep-quality-score'],
      },
      {
        id: HealthScoreType.Stress,
        name: messages.stress,
        obs: data['stress-score'],
      },
    ];
    return healthTypes
      .filter((item) => Object.prototype.hasOwnProperty.call(data, item.id))
      .sort((a, b) => {
        const aExists = a.obs !== null ? 0 : 1;
        const bExists = b.obs !== null ? 0 : 1;

        if (aExists !== bExists) return aExists - bExists;
        return a.name.defaultMessage.localeCompare(b.name.defaultMessage);
      })
      .map((item) => ({
        id: item.id,
        name: item.name,
        /* obs.valueInteger is the score, obs.component[index].valueInteger are the contributors */
        score: typeof item.obs?.value === 'number' ? item.obs.value : null,
        date: item.obs?.getObservationDateStr(),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  return (
    <Box>
      <DashHeader>
        <HeaderSpacing>{formatMessage(messages.healthScores)}</HeaderSpacing>
      </DashHeader>

      <Grid2 container spacing={2}>
        {healthData.map((metric) => (
          <Grid2 size={{ xs: 12, sm: 12, md: 3 }} key={metric.id}>
            <SkeletonLoader loading={loading} height={150}>
              <HealthScoreCard metric={metric} />
            </SkeletonLoader>
          </Grid2>
        ))}
      </Grid2>
    </Box>
  );
};

export default withErrorBoundary(HealthScores, {
  fallback: null,
});
